import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { showToast } from '../utils/toast';
import './AccessPage.css'; // Use the new CSS file
import logo from '../assets/pokeshani-logo.png';
import SEO from './SEO';

const AccessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessData, setAccessData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [productSlug, setProductSlug] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsExpired(false);
        
        if (!token) {
          setError('Access token is required');
          return;
        }

        // Validate UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(token)) {
          setError('Invalid access token format');
          return;
        }

        // Fetch membership record with the access token
        // Using match instead of eq for better performance
        const { data: membershipData, error } = await supabase
          .from('membership')
          .select('id, digitalproducts_id, access_token, start_date, end_date, status, renewal_count, title')
          .match({ access_token: token });

        if (error) {
          console.error('Membership fetch error:', error);
          setError('Invalid or expired access token');
          return;
        }

        // Check if we have exactly one membership record
        if (!membershipData || membershipData.length === 0) {
          setError('Invalid or expired access token');
          return;
        }

        if (membershipData.length > 1) {
          console.error('Multiple membership records found for token:', token);
          setError('Invalid access token - multiple records found');
          return;
        }

        // Get the single membership record
        const membership = membershipData[0];

        // Check if membership is active
        if (membership.status !== 'active') {
          // If membership is expired, we still want to show renewal option
          if (membership.status === 'expired') {
            setIsExpired(true);
            // Fetch product slug for renewal
            const { data: product, error: productError } = await supabase
              .from('digitalproducts')
              .select('slug')
              .eq('id', membership.digitalproducts_id)
              .single();
            
            if (!productError && product) {
              setProductSlug(product.slug);
            }
          }
          setError('Membership is not active');
          return;
        }

        // Check if membership has expired
        const now = new Date();
        const endDate = new Date(membership.end_date);
        if (now > endDate) {
          setIsExpired(true);
          // Fetch product slug for renewal
          const { data: product, error: productError } = await supabase
            .from('digitalproducts')
            .select('slug')
            .eq('id', membership.digitalproducts_id)
            .single();
          
          if (!productError && product) {
            setProductSlug(product.slug);
          }
          setError('Your membership access has expired. Please renew to continue.');
          return;
        }

        // Fetch the digital product details including slug
        const { data: product, error: productError } = await supabase
          .from('digitalproducts')
          .select('access_url, title, creator_id, slug')
          .eq('id', membership.digitalproducts_id)
          .single();

        if (productError || !product) {
          setError('Product not found');
          return;
        }

        // Fetch only the required creator information: name, email, and social accounts
        const { data: creator, error: creatorError } = await supabase
          .from('profiles')
          .select('full_name, email, paypal_email, twitter_url, instagram_url')
          .eq('id', product.creator_id)
          .single();

        if (creatorError || !creator) {
          setError('Creator information not available');
          return;
        }

        const creatorName = creator.full_name || 'Creator';

        // Return access details with only the required information
        setAccessData({
          valid: true,
          access_url: product.access_url,
          product_title: product.title,
          product_slug: product.slug,
          creator_name: creatorName,
          creator_email: creator.paypal_email || creator.email || null,
          creator_twitter_url: creator.twitter_url || null,
          creator_instagram_url: creator.instagram_url || null,
          expiry_date: membership.end_date,
          renewal_count: membership.renewal_count
        });
      } catch (err) {
        console.error('Access validation error:', err);
        setError('Failed to validate access token');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleAccessContent = () => {
    if (accessData?.access_url) {
      window.location.href = accessData.access_url;
    }
  };

  const handleRenewal = () => {
    const slug = accessData?.product_slug || productSlug;
    if (slug) {
      window.location.href = `/sales/${slug}`;
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="access-landing-container">
        {token && (
          <SEO
            title="Access Validation - PokeShani"
            description="Validating subscription access token."
            keywords="access token, subscription, validation"
            url={`https://pokeshani.com/access/${token}`}
            robots="noindex, nofollow"
          />
        )}
        <div className="access-header-section compact">
          <div className="access-header-content compact">
            <a href="/" className="access-logo-link compact">
              <img src={logo} alt="PokeShani Logo" className="access-logo compact" />
            </a>
            <div>
              <h1 className="access-main-title compact">Access Validation</h1>
              <p className="access-subtitle compact">Verifying your subscription access</p>
            </div>
          </div>
        </div>
        
        <div className="access-main-content compact">
          <div className="access-card compact">
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Validating Access...</h2>
              <div className="loading-spinner compact"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Special handling for expired tokens
  if (isExpired) {
    return (
      <div className="access-landing-container">
        {token && (
          <SEO
            title="Access Expired - PokeShani"
            description="Your subscription access has expired."
            keywords="subscription expired, renewal"
            url={`https://pokeshani.com/access/${token}`}
            robots="noindex, nofollow"
          />
        )}
        <div className="access-header-section compact">
          <div className="access-header-content compact">
            <a href="/" className="access-logo-link compact">
              <img src={logo} alt="PokeShani Logo" className="access-logo compact" />
            </a>
            <div>
              <h1 className="access-main-title compact">Access Expired</h1>
              <p className="access-subtitle compact">Your subscription has expired</p>
            </div>
          </div>
        </div>
        
        <div className="access-main-content compact">
          <div className="error-message compact">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Subscription Expired</h2>
            <p style={{ fontSize: '0.95rem', marginBottom: '1rem' }}>
              Your access to this content has expired. You can renew your subscription to continue accessing this content.
            </p>
            
            <div className="button-group compact" style={{ gridTemplateColumns: '1fr', gap: '0.75rem', margin: '1rem 0' }}>
              <button 
                className="renewal-button compact" 
                onClick={handleRenewal}
                style={{ width: '100%' }}
              >
                Renew Subscription
              </button>
              <button 
                className="access-button compact" 
                onClick={handleBackToHome}
                style={{ width: '100%' }}
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="access-landing-container">
        {token && (
          <SEO
            title="Access Error - PokeShani"
            description="There was an issue with your access."
            keywords="access error"
            url={`https://pokeshani.com/access/${token}`}
            robots="noindex, nofollow"
          />
        )}
        <div className="access-header-section compact">
          <div className="access-header-content compact">
            <a href="/" className="access-logo-link compact">
              <img src={logo} alt="PokeShani Logo" className="access-logo compact" />
            </a>
            <div>
              <h1 className="access-main-title compact">Access Error</h1>
              <p className="access-subtitle compact">There was an issue with your access</p>
            </div>
          </div>
        </div>
        
        <div className="access-main-content compact">
          <div className="error-message compact">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Access Error</h2>
            <p style={{ fontSize: '0.95rem', marginBottom: '1rem' }}>{error}</p>
            <button 
              className="access-button compact" 
              onClick={handleBackToHome}
              style={{ width: 'auto', display: 'inline-block' }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (accessData?.valid) {
    return (
      <div className="access-landing-container">
        {token && (
          <SEO
            title="Access Granted - PokeShani"
            description="You have access to your digital product."
            keywords="access granted, subscription"
            url={`https://pokeshani.com/access/${token}`}
            robots="noindex, nofollow"
          />
        )}
        <div className="access-header-section compact">
          <div className="access-header-content compact">
            <a href="/" className="access-logo-link compact">
              <img src={logo} alt="PokeShani Logo" className="access-logo compact" />
            </a>
            <div>
              <h1 className="access-main-title compact">Thank You for Your Purchase!</h1>
              <p className="access-subtitle compact">You now have access to {accessData.product_title}</p>
            </div>
          </div>
        </div>
        
        <div className="access-main-content compact">
          <div className="access-card compact">
            <div className="product-info compact">
              <h3>{accessData.product_title}</h3>
              <p style={{ margin: '0', color: '#6c757d' }}>by {accessData.creator_name}</p>
              
              <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '6px', borderRadius: '5px', margin: '8px auto', maxWidth: '220px' }}>
                <p style={{ margin: '0', fontSize: '0.85rem' }}><strong>Access Expires:</strong> {new Date(accessData.expiry_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
            
            {/* Action Buttons - Placed above creator information */}
            <div className="button-group compact">
              <button 
                className="access-button compact" 
                onClick={handleAccessContent}
              >
                Access Your Content
              </button>
              <button 
                className="renewal-button compact" 
                onClick={handleRenewal}
              >
                Renew Subscription
              </button>
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '0.8rem', color: '#a5b4fc' }}>
              <p>Click "Access Your Content" to access your digital product or "Renew Subscription" to extend your access.</p>
            </div>
            
            {/* Creator Information Section - Only name, email, and social accounts */}
            <div className="creator-info compact">
              <h4>Creator Information</h4>
              <div style={{ marginBottom: '8px', textAlign: 'center' }}>
                <p style={{ margin: '0 0 3px 0', fontSize: '0.9rem' }}><strong>Name:</strong> {accessData.creator_name}</p>
                {accessData.creator_email && (
                  <p style={{ margin: '0 0 3px 0', fontSize: '0.9rem' }}><strong>Email:</strong> {accessData.creator_email}</p>
                )}
              </div>
              
              {/* Social Links - Only Twitter and Instagram */}
              {(accessData.creator_twitter_url || accessData.creator_instagram_url) && (
                <div>
                  <p style={{ margin: '6px 0 3px 0', textAlign: 'center', fontSize: '0.85rem' }}><strong>Connect with Creator:</strong></p>
                  <div className="social-links compact" style={{ marginTop: '0.4rem' }}>
                    {accessData.creator_twitter_url && (
                      <a 
                        href={accessData.creator_twitter_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="social-button compact"
                      >
                        Twitter
                      </a>
                    )}
                    {accessData.creator_instagram_url && (
                      <a 
                        href={accessData.creator_instagram_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="social-button compact"
                      >
                        Instagram
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="access-landing-container">
      {token && (
        <SEO
          title="Access Information - PokeShani"
          description="Invalid access request."
          keywords="invalid access"
          url={`https://pokeshani.com/access/${token}`}
          robots="noindex, nofollow"
        />
      )}
      <div className="access-header-section">
        <div className="access-header-content">
          <a href="/" className="access-logo-link">
            <img src={logo} alt="PokeShani Logo" className="access-logo" />
          </a>
          <div>
            <h1 className="access-main-title">Access Information</h1>
            <p className="access-subtitle">Invalid access request</p>
          </div>
        </div>
      </div>
      
      <div className="access-main-content">
        <div className="access-card">
          <h2>Access Information</h2>
          <p>Invalid access request.</p>
        </div>
      </div>
    </div>
  );
};

export default AccessPage;
